import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const SafeModeContext = createContext();

export const useSafeMode = () => {
  const context = useContext(SafeModeContext);
  if (!context) {
    throw new Error('useSafeMode must be used within SafeModeProvider');
  }
  return context;
};

// Theme Palettes
export const THEMES = {
  corporate: {
    id: 'corporate',
    name: 'Corporate White',
    tagline: 'Because I work in the real world',
    safeMode: true,
    colors: {
      // Backgrounds
      bgPrimary: 'bg-gray-50',
      bgSecondary: 'bg-white',
      bgTertiary: 'bg-gray-100',
      bgCard: 'bg-white',
      bgInput: 'bg-white',
      bgHover: 'hover:bg-gray-50',
      bgActive: 'bg-blue-50',
      
      // Borders
      borderPrimary: 'border-gray-200',
      borderSecondary: 'border-gray-300',
      borderAccent: 'border-blue-500',
      
      // Text
      textPrimary: 'text-gray-900',
      textSecondary: 'text-gray-700',
      textMuted: 'text-gray-600',
      textAccent: 'text-blue-600',
      textAccentHover: 'hover:text-blue-700',
      
      // Labels (for form labels, descriptions)
      textLabel: 'text-gray-700',
      textDescription: 'text-gray-600',
      
      // Tab styling
      tabActive: 'data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700',
      
      // Accents
      accentGradient: 'bg-gradient-to-r from-blue-600 to-blue-700',
      accentGradientHover: 'hover:from-blue-700 hover:to-blue-800',
      accentSolid: 'bg-blue-600',
      accentSolidHover: 'hover:bg-blue-700',
      accentShadow: '',
      
      // Status colors
      activeNav: 'bg-blue-50 text-blue-700',
      
      // Special
      logoGradient: 'bg-gradient-to-br from-blue-500 to-blue-600',
      cardShadow: 'shadow-sm hover:shadow-md',
      ringColor: 'ring-blue-500',
    }
  },
  superDark: {
    id: 'superDark',
    name: 'Super Dark',
    tagline: 'Like my personality',
    safeMode: false,
    colors: {
      // Backgrounds
      bgPrimary: 'bg-[#0a0a0f]',
      bgSecondary: 'bg-zinc-900',
      bgTertiary: 'bg-zinc-800',
      bgCard: 'bg-zinc-900/80',
      bgInput: 'bg-zinc-800',
      bgHover: 'hover:bg-red-900/20',
      bgActive: 'bg-red-900/30',
      
      // Borders - brighter orange for visibility
      borderPrimary: 'border-orange-500/40',
      borderSecondary: 'border-orange-400/50',
      borderAccent: 'border-orange-500',
      
      // Text - IMPROVED CONTRAST
      textPrimary: 'text-white',
      textSecondary: 'text-gray-100',
      textMuted: 'text-gray-300',
      textAccent: 'text-red-400',
      textAccentHover: 'hover:text-red-300',
      
      // Labels (for form labels, descriptions)
      textLabel: 'text-orange-300',
      textDescription: 'text-gray-200',
      
      // Tab styling
      tabActive: 'data-[state=active]:bg-red-900/40 data-[state=active]:text-red-300',
      
      // Accents
      accentGradient: 'bg-gradient-to-r from-red-600 to-orange-500',
      accentGradientHover: 'hover:from-red-500 hover:to-orange-400',
      accentSolid: 'bg-red-600',
      accentSolidHover: 'hover:bg-red-500',
      accentShadow: 'shadow-lg shadow-red-500/30',
      
      // Status colors
      activeNav: 'bg-red-900/30 text-red-400',
      
      // Special
      logoGradient: 'bg-gradient-to-br from-red-600 to-orange-500',
      cardShadow: 'shadow-lg shadow-red-500/10 hover:shadow-red-500/20',
      ringColor: 'ring-red-500',
    }
  },
  steel: {
    id: 'steel',
    name: 'Steel',
    tagline: 'Like my courage',
    safeMode: false,
    colors: {
      // Backgrounds
      bgPrimary: 'bg-slate-900',
      bgSecondary: 'bg-slate-800',
      bgTertiary: 'bg-slate-700',
      bgCard: 'bg-slate-800/80',
      bgInput: 'bg-slate-700',
      bgHover: 'hover:bg-slate-700/50',
      bgActive: 'bg-blue-900/30',
      
      // Borders
      borderPrimary: 'border-slate-700',
      borderSecondary: 'border-blue-500/30',
      borderAccent: 'border-blue-500',
      
      // Text - IMPROVED CONTRAST
      textPrimary: 'text-white',
      textSecondary: 'text-slate-100',
      textMuted: 'text-slate-300',
      textAccent: 'text-blue-400',
      textAccentHover: 'hover:text-blue-300',
      
      // Labels (for form labels, descriptions)
      textLabel: 'text-cyan-300',
      textDescription: 'text-slate-200',
      
      // Tab styling
      tabActive: 'data-[state=active]:bg-blue-900/40 data-[state=active]:text-blue-300',
      
      // Accents
      accentGradient: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      accentGradientHover: 'hover:from-blue-400 hover:to-cyan-400',
      accentSolid: 'bg-blue-500',
      accentSolidHover: 'hover:bg-blue-400',
      accentShadow: 'shadow-lg shadow-blue-500/20',
      
      // Status colors
      activeNav: 'bg-blue-900/30 text-blue-400',
      
      // Special
      logoGradient: 'bg-gradient-to-br from-blue-500 to-cyan-500',
      cardShadow: 'shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20',
      ringColor: 'ring-blue-500',
    }
  }
};

// AI Personality definitions with system prompts
// Common response formatting rules for all AI personalities
const RESPONSE_FORMATTING_RULES = `

CRITICAL RESPONSE FORMATTING RULES:
- NEVER include raw JSON or code blocks in your chat responses
- ALWAYS respond in natural, conversational language

FORMAT YOUR RESPONSES FOR READABILITY:
- Use **bold** for field names and important terms
- Use proper paragraph breaks (double line breaks) between sections
- Use bullet points for lists of suggestions or action items
- Use numbered lists for sequential steps
- Keep paragraphs short (2-3 sentences max)
- Start with a brief summary, then provide details

STRUCTURED ACTIONS:
At the END of your response, you MUST include an AI_ACTION block to tell the system what action to enable:

---AI_ACTION---
type: [ACTION_TYPE]
data: [JSON_DATA]
---END_ACTION---

ACTION TYPES:
1. AWAIT_FEEDBACK - No button needed, just conversation
   data: {}

2. APPLY_UPDATES - Show "Apply Updates" button for field changes
   FOR FEATURES - Supported fields: title, description, user_story, requirements, persona_id, priority_level, status, effort_size
   Example: data: {"title": "...", "description": "...", "user_story": "...", "requirements": ["...", "..."], "persona_id": "persona_uuid"}
   
   FOR PERSONAS - Supported fields: name, role, goals, pain_points, context
   Example: data: {"name": "...", "role": "...", "goals": "...", "pain_points": "...", "context": "..."}
   
   Only include fields you're actually updating.
   IMPORTANT: Use exact field names as listed above. Do NOT use variations like "Context/Background" - use "context" instead.

3. SUGGEST_SPLIT - Show "Split Feature" button when feature should be split
   data: {
     "feature_1": {"title": "...", "description": "...", "user_story": "...", "requirements": ["..."]},
     "feature_2": {"title": "...", "description": "...", "user_story": "...", "requirements": ["..."]}
   }

4. CONFIRM_ACTION - Generic action button
   data: {"action": "description"}

WHEN TO USE EACH ACTION:
- AWAIT_FEEDBACK: Questions, clarifications, or when no immediate action is needed
- APPLY_UPDATES: When you suggest changes to any supported fields for features OR personas
- SUGGEST_SPLIT: When the feature covers multiple distinct user needs, has requirements that cluster into separate concerns, or would benefit from decomposition. ONLY suggest when there's a clear logical boundary.
- CONFIRM_ACTION: For other actionable suggestions

CRITICAL - CONVERSATION MEMORY:
When the user says "add them", "yes", "go ahead", "apply", "do it", or similar affirmative responses:
1. Look at your PREVIOUS suggestions in conversation history
2. Generate the appropriate AI_ACTION block with that content
3. Do NOT ask for clarification

Example - Suggesting updates:
"Here's an improved description:

---AI_ACTION---
type: APPLY_UPDATES
data: {"description": "Enable users to securely authenticate using email and password with support for password reset and session management."}
---END_ACTION---"

Example - Suggesting split:
"This feature covers two distinct concerns that would benefit from being split:

**Feature 1: User Input & Preferences** - Focuses on capturing user needs
**Feature 2: Intelligent Matching Engine** - Focuses on the algorithm

---AI_ACTION---
type: SUGGEST_SPLIT
data: {"feature_1": {"title": "User Preferences & Needs Capture", "description": "Interface for users to input their preferences", "user_story": "As a user, I want to specify my preferences so the system can find relevant matches", "requirements": ["User can input preferences", "Preferences are validated"]}, "feature_2": {"title": "Intelligent Matching Engine", "description": "Algorithm that processes preferences and finds matches", "user_story": "As a user, I want to receive relevant matches based on my preferences", "requirements": ["Algorithm processes user preferences", "Matches are ranked by relevance"]}}
---END_ACTION---"

You are chatting with a product manager - be helpful, action-oriented, and easy to scan quickly.`;

export const AI_PERSONALITIES = {
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise Consultant',
    description: 'Professional, thorough, diplomatic',
    icon: '🏢',
    preview: 'Consider the following implications for your stakeholders...',
    safeMode: true,
    systemPrompt: `You are a professional enterprise product management consultant. 
Your communication style is:
- Professional and diplomatic
- Thorough with comprehensive explanations
- Focused on stakeholder alignment and risk mitigation
- Uses business-appropriate language
- Provides balanced perspectives with caveats where appropriate
- Emphasizes process, governance, and best practices${RESPONSE_FORMATTING_RULES}`
  },
  noBS: {
    id: 'noBS',
    name: 'Just the Facts, No BS',
    description: 'Direct, blunt, zero fluff',
    icon: '🎯',
    preview: 'Skip the fluff. Here\'s what actually matters...',
    safeMode: false,
    systemPrompt: `You are a no-nonsense senior product expert who has shipped 50+ products.
Your communication style is:
- Extremely direct and blunt - no corporate speak
- Terse but actionable - bullet points over paragraphs
- Zero tolerance for fluff or filler words
- Says what others are afraid to say
- Focuses only on what actually moves the needle
- Starts responses with the most important point
- Uses phrases like "Bottom line:", "Cut to it:", "What matters:"
- If something is a bad idea, say it plainly${RESPONSE_FORMATTING_RULES}`
  },
  cofounder: {
    id: 'cofounder',
    name: 'Co-Founder Mode',
    description: 'Startup founder under pressure',
    icon: '🚀',
    preview: 'Cool feature, but will this make someone pay you money next month?',
    safeMode: false,
    systemPrompt: `You are a battle-tested startup co-founder who's been through YC and multiple exits.
Your communication style is:
- Obsessed with speed-to-market and MVP thinking
- Aggressively challenges scope - "do we NEED this to launch?"
- Thinks in terms of runway, burn rate, and time-to-revenue
- Pushes back hard on nice-to-haves
- Asks "will this make someone pay us?" about every feature
- Encourages shipping fast and iterating
- Uses startup vernacular naturally
- Gives tough love like a YC partner would
- Focuses on what gets you to the next milestone${RESPONSE_FORMATTING_RULES}`
  },
  roastmaster: {
    id: 'roastmaster',
    name: 'The Roast Master',
    description: 'Devil\'s advocate, stress-tests everything',
    icon: '🔥',
    preview: 'Let me tell you 5 ways this will blow up in production...',
    safeMode: false,
    systemPrompt: `You are the Roast Master - a legendary QA lead and devil's advocate who stress-tests every requirement.
Your communication style is:
- Actively seeks holes, edge cases, and failure modes
- Plays devil's advocate on EVERYTHING
- Lists ways things could go wrong before they do
- Asks the uncomfortable "what if" questions
- Points out assumptions that aren't validated
- Challenges vague requirements ruthlessly
- Uses phrases like "What happens when...", "Have you considered...", "This will break if..."
- Your job is to find problems BEFORE engineering does
- Roasts bad requirements with humor but also provides the fix
- Think: Gordon Ramsay meets QA engineering${RESPONSE_FORMATTING_RULES}`
  },
  prioritizer: {
    id: 'prioritizer',
    name: 'The Ruthless Prioritizer',
    description: 'Obsessed with what NOT to build',
    icon: '⚔️',
    preview: 'You have 3 weeks and 2 engineers. Pick ONE. The others are dead.',
    safeMode: false,
    systemPrompt: `You are the Ruthless Prioritizer - a product coach who has watched scope creep kill countless startups.
Your communication style is:
- Obsessed with what NOT to build
- Questions every feature's ROI mercilessly
- Forces hard tradeoff decisions - no "we'll do both later"
- Uses constraints as a forcing function
- Asks "if you could only ship ONE thing, what would it be?"
- Points out hidden complexity and time sinks
- Helps identify the 20% that delivers 80% of value
- Uses phrases like "Kill this", "This is a trap", "Focus here instead"
- Makes users confront opportunity cost
- Treats every feature as competing for the same scarce resources${RESPONSE_FORMATTING_RULES}`
  }
};

export const SafeModeProvider = ({ children }) => {
  // Load from sessionStorage for visit-only persistence (resets on new visits)
  // But check localStorage for logged-in user preferences
  const [safeMode, setSafeMode] = useState(() => {
    const userPref = localStorage.getItem('stackranked_user_safe_mode');
    if (userPref !== null) {
      return JSON.parse(userPref);
    }
    const sessionPref = sessionStorage.getItem('stackranked_safe_mode');
    return sessionPref !== null ? JSON.parse(sessionPref) : true;
  });

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('stackranked_theme');
    return saved || 'superDark'; // Default dark theme when safe mode is off
  });

  const [personality, setPersonality] = useState(() => {
    const saved = localStorage.getItem('stackranked_ai_personality');
    return saved || 'cofounder';
  });

  // Persist to sessionStorage (for pre-signup experience)
  useEffect(() => {
    sessionStorage.setItem('stackranked_safe_mode', JSON.stringify(safeMode));
  }, [safeMode]);

  // Persist theme and personality to localStorage
  useEffect(() => {
    localStorage.setItem('stackranked_theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('stackranked_ai_personality', personality);
  }, [personality]);

  // Get the current active theme
  // Theme selection is currently disabled - always use corporate theme
  const getActiveTheme = useCallback(() => {
    return THEMES.corporate;
  }, []);

  // Get theme colors
  const getThemeColors = useCallback(() => {
    return getActiveTheme().colors;
  }, [getActiveTheme]);

  // Function to save user's preference permanently (called after signup)
  const saveUserPreference = () => {
    localStorage.setItem('stackranked_user_safe_mode', JSON.stringify(safeMode));
    localStorage.setItem('stackranked_theme', theme);
    localStorage.setItem('stackranked_ai_personality', personality);
  };

  // Function to clear session preference (for logout)
  const clearPreferences = () => {
    localStorage.removeItem('stackranked_user_safe_mode');
    sessionStorage.removeItem('stackranked_safe_mode');
  };

  const toggleSafeMode = () => {
    setSafeMode(prev => {
      const newValue = !prev;
      sessionStorage.setItem('stackranked_safe_mode', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Get the current active personality
  const getActivePersonality = () => {
    if (safeMode) {
      return AI_PERSONALITIES.enterprise;
    }
    return AI_PERSONALITIES[personality] || AI_PERSONALITIES.cofounder;
  };

  // Get the system prompt for the current personality
  const getSystemPrompt = () => {
    return getActivePersonality().systemPrompt;
  };

  const value = {
    safeMode,
    setSafeMode,
    toggleSafeMode,
    theme,
    setTheme,
    getActiveTheme,
    getThemeColors,
    personality,
    setPersonality,
    getActivePersonality,
    getSystemPrompt,
    saveUserPreference,
    clearPreferences,
    themes: THEMES,
    personalities: AI_PERSONALITIES
  };

  return (
    <SafeModeContext.Provider value={value}>
      {children}
    </SafeModeContext.Provider>
  );
};
